import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React, { Component } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import { getOpenerContent, isItemSelected } from '@commons/Dropdown/Simple/common';
import styles from '@commons/Dropdown/dropdown.scss';
import Icon, { ICON_TYPES } from '@commons/Icon';

import layoutStyles from '@css/layout.scss';

import {
  isA11yWfcEnhancementEnabled,
  isDropdownAccessibilityEnabled,
  isRankingOrderAccessibilityEnabled
} from '@services/featureFlags';

import {
  getKey,
  isArrowDownKey,
  isArrowUpKey,
  isEnterKey,
  isEsc,
  isSpaceKey,
  isTab
} from '@utils/detectKey';
import stripHtml from '@utils/stripHtml';

import DesktopItem from './Item';

interface DesktopDropdownProps {
  dropdownList?: unknown;
  label?: string;
  isDropdownLabelInactive?: boolean;
  title?: string;
  selected?: unknown;
  selectItem?: (...args: unknown[]) => unknown;
  containerStyles?: string;
  validationEmpty?: string;
  validationFailed?: string;
  requiredField?: string;
  disabled?: boolean;
  selectByWordWhenIsOpenOnly?: boolean;
  isMultiValued?: boolean;
  onDropDownOpen?: (...args: unknown[]) => unknown;
  onDropDownClose?: (...args: unknown[]) => unknown;
}

class DesktopDropdown extends Component<DesktopDropdownProps> {
  static defaultProps = {
    dropdownList: [],
    label: '',
    isDropdownLabelInactive: false,
    title: '',
    selected: null,
    selectItem: noop,
    disabled: false,
    selectByWordWhenIsOpenOnly: false,
    isMultiValued: false,
    onDropDownOpen: noop,
    onDropDownClose: noop
  };

  constructor(props) {
    super(props);
    this.state = {
      listInvisible: true,
      focusedIndex: 0
    };

    this.buffer = '';

    this.debouncedGetItemMatchingKeyPressed = debounce(
      this.getItemMatchingKeyPressed.bind(this),
      300
    );
  }

  _documentKeydownListener = (e) => {
    // If Escape or Tab is pressed anywhere while the dropdown is open,
    // close it. Prevent default and stop propagation so the
    // browser/VoiceOver doesn't perform its default restore action.
    if (isEsc(e) || isTab(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.handleHide(true);
    }
  };

  addDocumentKeydownListener = () => {
    // Uses capture phase so we get the key before other handlers.
    document.addEventListener('keydown', this._documentKeydownListener, true);
  };

  removeDocumentKeydownListener = () => {
    document.removeEventListener('keydown', this._documentKeydownListener, true);
  };

  componentWillUnmount() {
    this.removeDocumentKeydownListener();
  }

  setRefDropdownSelector = (dropdownSelector) => {
    this._$dropdownSelector = dropdownSelector;
  };

  isEnterSpaceArrowDownKey = (e) => {
    return isEnterKey(e) || isSpaceKey(e) || isArrowDownKey(e);
  };

  handleKeyPressOnContainer = (event) => {
    if ((isEsc(event) || isTab(event)) && !this.state.listInvisible) {
      return this.handleHide(true);
    }
    if (!this.isEnterSpaceArrowDownKey(event)) {
      const keyPressCharacter = String.fromCharCode(getKey(event)).toString();
      if (keyPressCharacter) {
        if (this.props.selectByWordWhenIsOpenOnly && this.state.listInvisible) {
          return;
        }
        this.buffer = this.buffer.concat(keyPressCharacter);
        return this.debouncedGetItemMatchingKeyPressed(!this.state.listInvisible);
      }
    }

    event.preventDefault();
    return this.handleShow(this.state.focusedIndex); // pass updated focusedIndex instead of Default "0"
  };

  onDropDownOpenWithRankingOrderAccessibilityEnabled = () => {
    if (isRankingOrderAccessibilityEnabled()) {
      this.props.onDropDownOpen(this.state.focusedIndex);
    }
  };

  /*
   * Same method is called from onClick handler without passing isFocus
   * */
  // TODO: remove with onClickOutside
  // On DropdownOpen, handleShow was called with parameter focusedIndex=-1 and hence we were hearing Reordering at position unset or "0" instead of current rank set.
  handleShow = (focusedIndex) => {
    const { disabled, dropdownList } = this.props;
    if (disabled || !dropdownList || !dropdownList.length) {
      return;
    }

    this.setState({
      listInvisible: false,
      focusedIndex
    });
    // Add a document level keydown listener to catch Escape before the VoiceOver
    // or the browser/other handlers run and restore focus/perform their default actions.
    this.addDocumentKeydownListener();
  };

  // TODO: remove with onClickOutside
  // On DropdownClose, removed resetting of focusedIndex to -1 in the state as this was main cause of the unexpected behaviour i.e screen reader was announcing Reordering at position unset or "0" on dropdown open instead of current rank set.
  handleHide = (isCloseWithoutSelection) => {
    if (this.state.listInvisible) {
      return;
    }

    this.setState(
      {
        listInvisible: true
      },
      () => {
        if (isRankingOrderAccessibilityEnabled() && isCloseWithoutSelection) {
          this.props.onDropDownClose();
        }

        if (isRankingOrderAccessibilityEnabled() && !isNil(this._$dropdownSelector)) {
          this._$dropdownSelector.focus();
        } else if (!isNil(this._$dropdownSelector)) {
          this._$dropdownSelector.focus();
        }
        this.removeDocumentKeydownListener();
      }
    );
  };

  handleArrowDown = () => {
    const { focusedIndex } = this.state;
    const { dropdownList } = this.props;
    if (focusedIndex >= dropdownList.length - 1) return;
    this.setState({
      focusedIndex: focusedIndex + 1
    });
  };

  handleArrowUp = () => {
    const { focusedIndex } = this.state;
    if (focusedIndex <= 0) return;
    this.setState({
      focusedIndex: focusedIndex - 1
    });
  };

  handleKeyPressSelectItem = (event, item) => {
    if (isArrowDownKey(event)) {
      this.handleArrowDown();
    } else if (isArrowUpKey(event)) {
      this.handleArrowUp();
    } else if (isEsc(event) || isTab(event)) {
      event.preventDefault();
      // esc key or tab
      this.handleHide(true);
    } else if (isEnterKey(event) || isSpaceKey(event)) {
      event.preventDefault();
      this.selectItem(item);
    }
    if (!this.props.selectByWordWhenIsOpenOnly || !this.state.listInvisible) {
      this.selectByWord(event);
    }
  };

  selectItem = (item) => {
    this.props.selectItem(item);
    this.handleHide(false);
  };

  selectByWord = (event) => {
    const keyPressCharacter = String.fromCharCode(getKey(event)).toString();
    if (keyPressCharacter) {
      this.buffer = this.buffer.concat(keyPressCharacter);
      this.debouncedGetItemMatchingKeyPressed(true);
    }
  };

  getItemIndexMatchingBuffer = (buffer) => {
    return this.props.dropdownList.findIndex((item) => {
      const matchingText = item.text && item.text.toLowerCase().substr(0, buffer.length);
      return matchingText === buffer.toLowerCase();
    });
  };

  getItemMatchingKeyPressed = (focusOnly) => {
    const bufferMatchedItemIndex = this.getItemIndexMatchingBuffer(this.buffer);
    this.buffer = '';
    if (bufferMatchedItemIndex === -1) return null;
    if (focusOnly) {
      this.setState({
        focusedIndex: bufferMatchedItemIndex
      });

      // This previously relied on setState's `undefined` return,
      // which is misleading.
      return null;
    }

    this.selectItem(this.props.dropdownList[bufferMatchedItemIndex]);
  };

  getAriaLabelObject = () => {
    const { ariaLabel, selected } = this.props;

    if (!isDropdownAccessibilityEnabled()) {
      return ariaLabel;
    }

    if (!ariaLabel || !ariaLabel['aria-label']) {
      return {};
    }

    // Removes ":" at the end of the string
    const cleanAriaLabelTitle = ariaLabel['aria-label'].trim().replace(/:$/, '');

    if (isA11yWfcEnhancementEnabled()) {
      const ariaLabelTextSelectedOptionFirst =
        selected && selected.text
          ? `${selected.text}, ${cleanAriaLabelTitle}:`
          : cleanAriaLabelTitle;

      return { 'aria-label': ariaLabelTextSelectedOptionFirst };
    }

    const ariaLabelText =
      selected && selected.text ? `${cleanAriaLabelTitle}: ${selected.text}` : cleanAriaLabelTitle;

    return { 'aria-label': ariaLabelText };
  };

  render() {
    const {
      isDropdownLabelInactive,
      label,
      dropdownList,
      title,
      containerStyles,
      selectorStyles,
      calendarTitleStyles,
      calendarIconStyles,
      ariaRequired,
      ariaDisabled,
      ariaInvalid,
      disabled,
      selected,
      elementId,
      isMultiValued,
      isSubComponent
    } = this.props;

    const dropdownSelectorClasses = classNames(styles.dropdownSelector, {
      [styles.dropdownSelector_isDisable]: disabled,
      [styles.dropdownSelector_isInactive]: this.state.listInvisible
    });

    const labelClasses = classNames(styles.dropdownLabel, {
      [styles.dropdownLabel_isInactive]: isDropdownLabelInactive
    });

    const listClasses = classNames(styles.dropdownList, {
      [styles.dropdownList_isActive]: !this.state.listInvisible
    });

    const openerAriaAttributes = {
      role: 'button',
      tabIndex: '0',
      'aria-haspopup': 'true',
      'aria-expanded': !this.state.listInvisible,
      ...ariaInvalid,
      ...ariaRequired,
      ...ariaDisabled
    };

    const ariaLabelObject = this.getAriaLabelObject();
    const openerAttributes = isDropdownAccessibilityEnabled()
      ? {
          type: 'button',
          id: elementId,
          ...ariaLabelObject
        }
      : {
          title: stripHtml(title),
          ...ariaLabelObject
        };

    const listRole = 'menu';
    const DropdownOpener = isDropdownAccessibilityEnabled() ? 'button' : 'div';
    const OpenerContent = isDropdownAccessibilityEnabled() ? 'span' : 'div';
    const listContainerRoleObject = isDropdownAccessibilityEnabled() ? {} : { role: 'application' };

    const labelComponent = isDropdownAccessibilityEnabled() ? (
      <label htmlFor={elementId} className={labelClasses}>
        {label}
      </label>
    ) : (
      <div className={labelClasses}>{label}</div>
    );

    return (
      <div className={layoutStyles.notMobileScreenOnly}>
        {label && labelComponent}
        <div className={`${styles.dropdownContainer} ${containerStyles}`}>
          <DropdownOpener
            className={classNames(dropdownSelectorClasses, selectorStyles)}
            ref={this.setRefDropdownSelector}
            onClick={() => {
              this.handleShow(this.state.focusedIndex);
            }}
            onKeyDown={this.handleKeyPressOnContainer}
            {...openerAriaAttributes}
            {...openerAttributes}
          >
            <OpenerContent className={calendarTitleStyles}>
              {getOpenerContent(this.props)}
            </OpenerContent>
            <Icon containerStyles={calendarIconStyles} type={ICON_TYPES.ARROW_DROPDOWN} />
          </DropdownOpener>
          <OutsideClickHandler onOutsideClick={() => this.handleHide(true)}>
            <div {...listContainerRoleObject}>
              <ul
                role={listRole}
                className={listClasses}
                tabIndex={isA11yWfcEnhancementEnabled() ? '-1' : null}
              >
                {dropdownList.map((item, index) => (
                  <DesktopItem
                    onDropDownOpenWithRankingOrderAccessibilityEnabled={
                      this.onDropDownOpenWithRankingOrderAccessibilityEnabled
                    }
                    key={item.id || index}
                    selected={isItemSelected({ itemId: item.id, isMultiValued, selected })}
                    selectItem={this.selectItem}
                    item={item}
                    handleKeyPressSelectItem={this.handleKeyPressSelectItem}
                    focused={index === this.state.focusedIndex}
                    isMultiValued={isMultiValued}
                    isSubComponent={isSubComponent}
                  />
                ))}
              </ul>
            </div>
          </OutsideClickHandler>
        </div>
      </div>
    );
  }
}

export default DesktopDropdown;
